/**
 * IMPORTS
 */

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { BarChart3, MapPin, Receipt, Undo2, User } from "lucide-react";

// components
import { Button } from "@components/button/button";
import { Spinner } from "@components/spinner/spinner";
import { Tabs, TabsList, TabsTrigger } from "@components/tabs";

// screens / pages
import { CobrancaCreditosCliente } from "@pages/admin/editar-dados-cliente/cobranca-creditos-cliente";
import { DadosCliente } from "@pages/admin/editar-dados-cliente/dados-cliente";
import { Enderecos } from "@pages/admin/editar-dados-cliente/enderecos";
import { ExtratoMovimentacoesCreditoCliente } from "@pages/admin/editar-dados-cliente/extrato-movimentacoes-credito-cliente";
import { UsuariosCliente } from "@pages/admin/editar-dados-cliente/usuarios-cliente";

// utils
import { FEATURES, useFeature } from "@/utils/auth/features";

type EstadoNavegacaoEditarCliente = {
  cliente?: { id?: number };
  abaInicial?: string;
};

const EditarDadosCliente = () => {
  const router = useNavigate();
  const location = useLocation();

  const [selectedTab, setSelectedTab] = useState("dados-cliente");

  const [isLoading] = useState(false);

  const usuarioPodeVerExtratoCredito = useFeature(FEATURES.VISUALIZAR_EXTRATO_CREDITO);
  const usuarioPodeVerCobrancaCredito = useFeature(FEATURES.VISUALIZAR_COBRANCA_CREDITO);

  useEffect(() => {
    const estado = location.state as EstadoNavegacaoEditarCliente | null;
    if (usuarioPodeVerExtratoCredito && estado?.abaInicial === "extrato-movimentacoes-credito") {
      setSelectedTab("extrato-movimentacoes-credito");
    }
    if (usuarioPodeVerCobrancaCredito && estado?.abaInicial === "cobranca-creditos") {
      setSelectedTab("cobranca-creditos");
    }
  }, [usuarioPodeVerExtratoCredito, usuarioPodeVerCobrancaCredito, location.state]);

  useEffect(() => {
    if (!usuarioPodeVerExtratoCredito && selectedTab === "extrato-movimentacoes-credito") {
      setSelectedTab("dados-cliente");
    }
    if (!usuarioPodeVerCobrancaCredito && selectedTab === "cobranca-creditos") {
      setSelectedTab("dados-cliente");
    }
  }, [usuarioPodeVerExtratoCredito, usuarioPodeVerCobrancaCredito, selectedTab]);

  return (
    <>
      {isLoading ? (
        <Spinner />
      ) : (
        <Tabs value={selectedTab} onValueChange={setSelectedTab}>
          <div className="flex items-center">
            <div className="mb-4 ml-auto mr-4 flex items-center gap-2">
              <Button
                size="sm"
                variant="default"
                className="h-8 gap-1 text-neutral-50"
                onClick={() => router("/manage-clients")}
              >
                <Undo2 className="h-3.5 w-3.5" />
                <span className="sr-only text-neutral-50 sm:not-sr-only sm:whitespace-nowrap">
                  Voltar
                </span>
              </Button>
            </div>
          </div>

          <div className="mx-auto w-full rounded-lg bg-white p-6 shadow-md">
            <TabsList className="mb-4 bg-white">
              <TabsTrigger value="dados-cliente">
                <User className="mr-1 h-4 w-4" />
                Dados
              </TabsTrigger>
              <TabsTrigger value="enderecos">
                <MapPin className="mr-1 h-4 w-4" />
                Endereços
              </TabsTrigger>
              {usuarioPodeVerExtratoCredito && (
                <TabsTrigger value="extrato-movimentacoes-credito">
                  <Receipt className="mr-1 h-4 w-4" />
                  Extrato de créditos
                </TabsTrigger>
              )}
              {usuarioPodeVerCobrancaCredito && (
                <TabsTrigger value="cobranca-creditos">
                  <BarChart3 className="mr-1 h-4 w-4" />
                  Cobrança de créditos
                </TabsTrigger>
              )}
              {/* <TabsTrigger value="usuarios">
                <Users className="w-4 h-4 mr-1" />
                Usuários do cliente
              </TabsTrigger> */}
            </TabsList>

            {/* Renderiza o conteúdo da aba selecionada */}
            {selectedTab === "dados-cliente" && <DadosCliente />}
            {selectedTab === "enderecos" && <Enderecos />}
            {usuarioPodeVerExtratoCredito && selectedTab === "extrato-movimentacoes-credito" && (
              <ExtratoMovimentacoesCreditoCliente />
            )}
            {usuarioPodeVerCobrancaCredito && selectedTab === "cobranca-creditos" && (
              <CobrancaCreditosCliente />
            )}
            {selectedTab === "usuarios" && <UsuariosCliente />}
          </div>
        </Tabs>
      )}
    </>
  );
};

export { EditarDadosCliente };
